import { AfterViewInit, Directive, ElementRef, HostListener, OnDestroy } from '@angular/core';

@Directive({
  selector: '[appTrapFocus]',
  standalone: false
})
export class TrapFocusDirective implements AfterViewInit, OnDestroy {
  constructor(private readonly el: ElementRef) {}

  ngAfterViewInit(): void {
    // Used to set focus on the first accessible element
    this.trapFocus(this.el.nativeElement);
  }

  /**
   * Used to move to first focusable element on initialization
   * Does not allow focus to go out of modal ((Supporting Accessibility))
   * See: https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/
   * @param element HTMLElement
   */
  trapFocus(element: HTMLElement): void {
    setTimeout((): void => {
      const focusableElements: HTMLElement[] = this.getFocusableElements();
      const currentlyFocusedElement = document.activeElement as HTMLElement;
      if (!element.contains(currentlyFocusedElement)) {
        focusableElements[0].focus();
      }
    }, 300);
  }

  @HostListener('keydown', ['$event'])
  handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Tab') {
      const focusableElements: HTMLElement[] = this.getFocusableElements();
      if (focusableElements.length === 0) {
        event.preventDefault();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const shouldMoveToFirstElement = !event.shiftKey && document.activeElement === lastElement;
      const shouldMoveToLastElement = event.shiftKey && document.activeElement === firstElement;

      if (shouldMoveToFirstElement) {
        event.preventDefault();
        firstElement.focus();
      } else if (shouldMoveToLastElement) {
        event.preventDefault();
        lastElement.focus();
      }
    }
  }

  ngOnDestroy(): void {
    this.el.nativeElement.removeEventListener('keydown', (): void => {});
  }

  private getFocusableElements(): HTMLElement[] {
    return Array.from(
      this.el.nativeElement.querySelectorAll(
        'a, button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select, [tabindex]:not([tabindex="-1"])'
      )
    );
  }
}
